import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Download,
  Loader2,
  Calendar,
  Sparkles,
  MessageSquareWarning,
  CheckCircle2,
  FileText,
} from "lucide-react";
import { api, BRL } from "@/lib/api";
import { Button } from "@/components/ui/button";

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + (iso.length === 10 ? "T12:00:00" : ""));
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
};

const statusMap = {
  active: { label: "Ativo", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  contested: { label: "Contestado", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  consolidated: { label: "Consolidado", cls: "bg-slate-100 text-slate-600 border-slate-300" },
  cancelled: { label: "Cancelado", cls: "bg-slate-100 text-slate-400 border-slate-200 line-through" },
};

function Thumb({ path, tag }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let obj;
    if (path) {
      api.get(`/files/${path}`, { responseType: "blob" })
        .then((r) => { obj = URL.createObjectURL(r.data); setSrc(obj); })
        .catch(() => setSrc(null));
    }
    return () => { if (obj) URL.revokeObjectURL(obj); };
  }, [path]);
  if (!src) {
    return (
      <div className="aspect-video rounded-lg border border-dashed border-slate-200 bg-slate-50 flex items-center justify-center text-[10px] text-slate-400">
        {tag}: sem foto
      </div>
    );
  }
  return (
    <a href={src} target="_blank" rel="noopener noreferrer" className="block">
      <div className="relative aspect-video rounded-lg overflow-hidden border border-slate-200">
        <img src={src} alt={tag} className="w-full h-full object-cover" />
        <span className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[9px] text-center py-0.5 font-bold uppercase tracking-wider">{tag}</span>
      </div>
    </a>
  );
}

/**
 * Página de prestação de contas FDP (admin e escola).
 * Mostra todos os lançamentos 1-a-1 com fotos antes/depois e botão PDF.
 */
export default function FdpEntriesDetail({ scope = "admin" }) {
  const { id: budgetId } = useParams();
  const navigate = useNavigate();
  const [budget, setBudget] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [b, e] = await Promise.all([
          api.get(scope === "school" ? `/school/budgets/${budgetId}` : `/budgets/${budgetId}`),
          api.get("/fdp-entries", { params: { budget_id: budgetId, include_consolidated: true } }),
        ]);
        setBudget(b.data);
        setEntries(e.data?.items || []);
      } catch (err) {
        toast.error("Falha ao carregar lançamentos");
      } finally {
        setLoading(false);
      }
    })();
  }, [budgetId, scope]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const r = await api.get(`/budgets/${budgetId}/fdp-entries-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error("Falha ao gerar PDF");
    } finally {
      setDownloading(false);
    }
  };

  const goBack = () => navigate(
    scope === "school" ? `/escola/orcamentos/${budgetId}` : `/orcamentos/${budgetId}`
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-500">
        <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Carregando...
      </div>
    );
  }

  const active = entries.filter((e) => e.status !== "cancelled");
  const total = active.reduce((acc, e) => acc + Number(e.value || 0), 0);

  return (
    <div className="space-y-4 pb-20" data-testid="fdp-entries-detail-page">
      {/* Header */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button variant="ghost" onClick={goBack} className="h-9 px-2" data-testid="btn-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> Voltar
        </Button>
        <h1 className="font-heading text-base sm:text-xl font-black text-slate-900 flex-1 min-w-0 leading-tight">
          Serviços executados — Orçamento {budget?.number ? `Nº ${budget.number}` : ""}
        </h1>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-3 sm:p-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-xs text-slate-500">{budget?.school?.name || ""}</p>
            <p className="text-sm font-black text-slate-900">
              {active.length} serviço{active.length !== 1 ? "s" : ""} · total <span className="text-indigo-700">{BRL(total)}</span>
            </p>
          </div>
        </div>
        <Button
          onClick={downloadPdf}
          disabled={downloading || active.length === 0}
          className="bg-indigo-600 hover:bg-indigo-700 text-white w-full sm:w-auto"
          data-testid="btn-download-fdp-pdf"
        >
          {downloading ? (
            <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Gerando...</>
          ) : (
            <><Download className="w-4 h-4 mr-1.5" /> Baixar PDF</>
          )}
        </Button>
      </div>

      {active.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center">
          <FileText className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">Nenhum lançamento FDP registrado neste orçamento.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="entries-detail-list">
          {active.map((e, idx) => {
            const b = statusMap[e.status] || statusMap.active;
            return (
              <div
                key={e.id}
                className="bg-white border border-slate-200 rounded-2xl p-3 sm:p-4"
                data-testid={`entry-detail-${e.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">#{idx + 1}</p>
                    <p className="text-sm sm:text-base font-bold text-slate-900 leading-snug">{e.description}</p>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1 text-[11px] text-slate-500">
                      <span className="inline-flex items-center gap-0.5">
                        <Calendar className="w-3 h-3" /> {formatDate(e.service_date)}
                      </span>
                      <span className={`inline-flex items-center gap-0.5 border rounded-full px-1.5 py-0.5 font-semibold ${b.cls}`}>
                        {b.label}
                      </span>
                      {e.note && <span className="italic text-slate-500">· {e.note}</span>}
                    </div>
                    {e.status === "contested" && e.contest_reason && (
                      <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-2 text-[11px] text-amber-900">
                        <p className="font-bold flex items-center gap-1">
                          <MessageSquareWarning className="w-3 h-3" /> Contestação da escola:
                        </p>
                        <p className="mt-0.5">{e.contest_reason}</p>
                      </div>
                    )}
                  </div>
                  <p className="shrink-0 text-base sm:text-lg font-black text-indigo-700 tabular-nums whitespace-nowrap">
                    {BRL(e.value)}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-3">
                  <Thumb path={e.before_photo_path} tag="Antes" />
                  <Thumb path={e.after_photo_path} tag="Depois" />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
